import { MarketCode } from '@schuettflix/utils';
import { PostOrderConfigurationRequest } from '@/shared/types/orderV2/api/post-order-configuration.ts';
import { FinanceSettings } from '@/shared/models/ContextDataModel/interfaces/entities/finance-settings.ts';
import { productTypeToTargetChannelMap } from '@schuettflix/product-group-utils';
import { ProductConfiguration } from '@/shared/models/ContextDataModel/interfaces/entities/product-configuration.ts';
import { BusinessCase, ORDER_SOURCE } from '@schuettflix/interfaces';
import { CustomerInfo } from '@/shared/models/ContextDataModel/interfaces/entities/customer-info.ts';

type ConstructOrderConfigurationParams = {
    customerInfo: CustomerInfo;
    businessCase: BusinessCase;
    financeSettings: FinanceSettings[];
    productConfigurations: ProductConfiguration[];
    marketCode: MarketCode;
    platformOrganizationId: number;
    source: Extract<ORDER_SOURCE, 'GENERIC_POSITION' | 'GENERIC_ORDER'>;
    position?: {
        id: string;
        number: string;
    };
};

export function constructOrderConfiguration({
    customerInfo,
    businessCase,
    financeSettings,
    productConfigurations,
    platformOrganizationId,
    marketCode,
    source,
    position,
}: ConstructOrderConfigurationParams): PostOrderConfigurationRequest {
    if (!customerInfo) {
        throw new Error(`Customer info is needed to make order configuration`);
    }

    if (!businessCase) {
        throw new Error(`Customer business case is needed to make order configuration`);
    }

    if (!customerInfo.projectNumber) {
        throw new Error(`Customer business case is needed to make order configuration`);
    }

    if (productConfigurations.length === 0) {
        throw new Error(`Product configurations are needed to make order configuration`);
    }

    if (financeSettings.length === 0) {
        throw new Error(`Finance settings are needed to make order configuration`);
    }

    return {
        customer: {
            constructionProjectId: customerInfo.constructionProjectId,
            organizationId: customerInfo.organizationId,
            userId: customerInfo.userId,
            projectCode: customerInfo.projectNumber,
            manualCostCenter: customerInfo.manualCostCenter,
        },
        businessCase,
        productConfigurations: productConfigurations.map(productConfiguration => ({
            ...productConfiguration,
            targetChannel: productTypeToTargetChannelMap[productConfiguration.productType],
            positionProductId: productConfiguration.id ?? null,
        })),
        financeSettings,
        marketCode,
        platformOrganizationId,
        positionId: position?.id ?? null,
        positionNumber: position?.number ?? null,
        source,
    };
}
